// src/pages/AmalanBurukDashboard.jsx
import React, { useEffect, useState, Fragment } from "react";
import { toast } from "react-toastify";
import API from "../../../services/API";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
} from "recharts";

// ==================== API HELPERS ====================
const fetchAmalanSummary = (params = {}) =>
    API.get("/badDeeds/summary", {
        headers: { token: localStorage.getItem("token") },
        params: { is_portal: true, ...params },
    });

const fetchAmalanStudentSummary = (params = {}) =>
    API.get("/badDeeds/studentSummary", {
        headers: { token: localStorage.getItem("token") },
        params: { is_portal: true, ...params },
    });

const fetchAmalanCategorySummary = (params = {}) =>
    API.get("/badDeeds/byCategory", {
        headers: { token: localStorage.getItem("token") },
        params: { is_portal: true, ...params },
    });

const fetchAmalanPendingStudents = (params = {}) =>
    API.get("/badDeeds/pendingByStudent", {
        headers: { token: localStorage.getItem("token") },
        params: { is_portal: true, ...params },
    });

const fetchAmalanStudentList = (params = {}) =>
    API.get("/badDeeds/studentList", {
        headers: { token: localStorage.getItem("token") },
        params: { is_portal: true, ...params },
    });
const fetchStudentBadDeeds = (student_uuid) =>
    API.get("/badDeeds/viewStudentAmalanBuruk", {
        headers: { token: localStorage.getItem("token") },
        params: { is_portal: true, student_uuid },
    });
const downloadStudentDisciplinePDF = (student_uuid) =>
    API.get("/badDeeds/getStudentDisciplineReportPDF", {
        headers: { token: localStorage.getItem("token") },
        params: { is_portal: true, student_uuid },
        responseType: "blob", // IMPORTANT
    });
const fetchAmalanFullSummary = (params = {}) =>
    API.get("/badDeeds/monthlySummary", {
        headers: { token: localStorage.getItem("token") },
        params: { is_portal: true, ...params },
    });

// ==================== COLORS ====================
const COLORS = [
    "#6366F1", "#22C55E", "#F59E0B", "#EF4444", "#0EA5E9",
    "#8B5CF6", "#EC4899", "#14B8A6", "#F97316", "#84CC16",
    "#3B82F6", "#10B981", "#EAB308", "#D946EF", "#06B6D4",
];

// ==================== COMPONENT ====================
const AmalanBurukDashboard = () => {
    const today = new Date();
    const currentMonth = today.toISOString().slice(0, 7);
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);

    const [overview, setOverview] = useState(null);
    const [trend, setTrend] = useState([]);
    const [topStudents, setTopStudents] = useState([]);
    const [categories, setCategories] = useState([]);
    const [pendingStudents, setPendingStudents] = useState([]);
    const [studentList, setStudentList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [studentDeeds, setStudentDeeds] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [monthlySummary, setMonthlySummary] = useState(null);
    const [filters, setFilters] = useState({
        name: "",
        class: "",
        reason: "",
        startDate: "",
        teacher: "",
        endDate: "",
    });


    const filteredCategories = categories.filter(
        (c) => c.category?.toLowerCase() !== "attendance"
    );

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const resSummary = await fetchAmalanSummary({ month: selectedMonth });
                if (resSummary.data.response_code === 1000) {
                    setOverview(resSummary.data.data.overview);
                    setTrend(resSummary.data.data.trend || []);
                } else toast.error(resSummary.data.message || "Failed to load summary");

                const resStudents = await fetchAmalanStudentSummary({
                    month: selectedMonth,
                    limit_top: 10,
                });
                if (resStudents.data.response_code === 1000)

                    setTopStudents(resStudents.data.data.leaderboard || []);

                const resCategories = await fetchAmalanCategorySummary({ month: selectedMonth });
                if (resCategories.data.response_code === 1000)
                    setCategories(resCategories.data.data || []);

                const resPending = await fetchAmalanPendingStudents({
                    month: selectedMonth,
                    limit_top: 10,
                });
                if (resPending.data.response_code === 1000)
                    setPendingStudents(resPending.data.data || []);

                const resStudentList = await fetchAmalanStudentList();
                if (resStudentList.data.response_code === 1000)
                    console.log(resStudentList.data.data[0].deed_time_myt)
                setStudentList(resStudentList.data.data || []);
                const resSummaryFull = await fetchAmalanFullSummary({ month: selectedMonth });
                if (resSummaryFull.data.response_code === 1000) {
                    console.log(resSummaryFull)
                    setMonthlySummary(resSummaryFull.data.data);
                }

            } catch (err) {
                toast.error(err?.message || "Failed to fetch Amalan Buruk analytics");
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [selectedMonth]);
    const handleStudentClick = async (student) => {
        setSelectedStudent(student);
        setShowModal(true);
        setStudentDeeds([]);
        try {
            const res = await fetchStudentBadDeeds(student.student_uuid);
            if (res.data.response_code === 1000) {
                console.log(res.data.data)
                setStudentDeeds(res.data.data || []);
            } else toast.error(res.data.message || "Failed to load details");
        } catch (err) {
            toast.error(err?.message || "Error fetching student details");
        }
    };
    // unique class list for dropdown
    const classOptions = Array.from(
        new Set(monthlySummary?.student_breakdown?.map((i) => i.class_name))
    ).filter(Boolean);
    const teacherOptions = Array.from(
        new Set(monthlySummary?.student_breakdown?.map((i) => i.teacher_name))
    ).filter(Boolean);

    return (
        <Fragment>
            <div className="container-fluid">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col d-flex align-items-center">
                        <h2 className="fw-bold text-danger">Masalah Disiplin Dashboard</h2>
                    </div>
                    <div className="col-auto">
                        <input
                            type="month"
                            className="form-control"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                        />
                    </div>
                </div>

                {/* Overview */}
                {overview && (
                    <div className="row mb-4">
                        {[
                            { label: "Total", value: overview.total_ops },
                            { label: "Students", value: overview.unique_students },
                            { label: "Teachers", value: overview.unique_teachers },
                        ].map((item, idx) => (
                            <div key={idx} className="col-6 col-md-4 col-lg-2 mb-3">
                                <div className="card text-center border-0 shadow-sm h-100 rounded-4">
                                    <div className="card-body">
                                        <h4 className="fw-bold mb-1">{item.value}</h4>
                                        <p className="text-muted small mb-0">{item.label}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="card-body">
                    <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                            <Pie
                                data={filteredCategories}
                                dataKey="total"
                                nameKey="category"
                                cx="50%"
                                cy="50%"
                                outerRadius={110}
                                label
                            >
                                {filteredCategories.map((entry, idx) => (
                                    <Cell
                                        key={idx}
                                        fill={COLORS[idx % COLORS.length]}
                                    />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend verticalAlign="bottom" align="center" layout="horizontal" />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                {/* ===================== Student Breakdown Table ===================== */}
                {monthlySummary?.student_breakdown && monthlySummary.student_breakdown.length > 0 && (
                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <div className="card-header bg-white rounded-top-4">
                            <h5 className="mb-0 text-danger">Perincian Kes Mengikut Murid</h5>
                        </div>
                        <div className="row g-2 m-2">
                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Cari Nama Murid..."
                                    value={filters.name}
                                    onChange={(e) =>
                                        setFilters((f) => ({ ...f, name: e.target.value }))
                                    }
                                />
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <select
                                    className="form-control form-control-sm"
                                    value={filters.class}
                                    onChange={(e) =>
                                        setFilters((f) => ({ ...f, class: e.target.value }))
                                    }
                                >
                                    <option value="">Kelas</option>

                                    {classOptions.map((cls, idx) => (
                                        <option key={idx} value={cls}>
                                            {cls}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <select
                                    className="form-control form-control-sm"
                                    value={filters.teacher}
                                    onChange={(e) =>
                                        setFilters((f) => ({ ...f, teacher: e.target.value }))
                                    }
                                >
                                    <option value="">Guru</option>

                                    {teacherOptions.map((t, idx) => (
                                        <option key={idx} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Cari Kesalahan..."
                                    value={filters.reason}
                                    onChange={(e) =>
                                        setFilters((f) => ({ ...f, reason: e.target.value }))
                                    }
                                />
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={filters.startDate}
                                    onChange={(e) =>
                                        setFilters((f) => ({ ...f, startDate: e.target.value }))
                                    }
                                />
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={filters.endDate}
                                    onChange={(e) =>
                                        setFilters((f) => ({ ...f, endDate: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <button
                                    className="btn btn-sm btn-outline-danger w-100"
                                    onClick={() =>
                                        setFilters({
                                            name: "",
                                            class: "",
                                            reason: "",
                                            teacher: "",
                                            startDate: "",
                                            endDate: "",
                                        })
                                    }
                                >
                                    Clear Filter
                                </button>
                            </div>

                        </div>

                        <div className="card-body table-responsive">
                            <table className="table table-sm table-striped align-middle">
                                <thead>
                                    <tr>
                                        <th>Tarikh</th>
                                        <th>Masa</th>
                                        <th>Nama Murid</th>
                                        <th>Kelas</th>
                                        <th>Kesalahan</th>
                                        <th>Catatan</th>
                                        <th>Guru</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...monthlySummary.student_breakdown]
                                        .filter((item) => {
                                            const date = new Date(item.date);

                                            return (
                                                // Name filter
                                                item.student_name?.toLowerCase().includes(filters.name.toLowerCase()) &&
                                                // Class filter
                                                item.class_name?.toLowerCase().includes(filters.class.toLowerCase()) &&
                                                // Reason filter
                                                item.deed_reason?.toLowerCase().includes(filters.reason.toLowerCase()) &&
                                                item.teacher_name?.toLowerCase().includes(filters.teacher.toLowerCase()) &&
                                                // Start date filter
                                                (!filters.startDate || date >= new Date(filters.startDate)) &&
                                                // End date filter
                                                (!filters.endDate || date <= new Date(filters.endDate))
                                            );
                                        })
                                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                                        .map((item, i) => (
                                            <tr key={i}>
                                                <td>{new Date(item.date).toLocaleDateString("ms-MY")}</td>
                                                <td>
                                                    {item.deed_time_myt
                                                        ? new Date(item.deed_time_myt).toLocaleTimeString("ms-MY", {
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                            hour12: true,
                                                        })
                                                        : "-"}
                                                </td>

                                                <td>{item.student_name || "-"}</td>
                                                <td>{item.class_name || "-"}</td>
                                                <td>{item.deed_reason || "-"}</td>
                                                <td>{item.remarks || "-"}</td>
                                                <td>{item.teacher_name || "-"}</td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                {/* Category Breakdown */}
                {filteredCategories.length > 0 && (
                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <div className="card-header bg-light rounded-top-4 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 text-danger">Category Breakdown</h5>
                            {selectedCategory && (
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setSelectedCategory(null)}
                                >
                                    Clear Filter
                                </button>
                            )}
                        </div>

                    </div>
                )}

                {/* Student List Section */}
                {studentList.length > 0 && (
                    <div className="card border-0 shadow-sm rounded-4 mb-2">
                        <div className="card-header bg-white rounded-top-4 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 text-danger">Senarai Murid</h5>
                            <span className="text-muted small">{studentList.length} murid</span>
                        </div>
                        {/* Disclaimer */}
                        <div className="p-2 align-items-center rounded-4">
                            <i className="bi bi-shield-lock-fill me-2 fs-5"></i>
                            <div>
                                <strong>Perhatian:</strong> Dashboard ini hanya boleh diakses oleh <b>Admin Portal</b>.
                                Data pelajar dan rekod disiplin adalah sulit dan untuk tujuan pentadbiran sekolah sahaja.
                            </div>
                        </div>
                        <div className="card-body table-responsive">
                            <table className="table table-hover align-middle">
                                <thead>
                                    <tr>
                                        <th>Nama Murid</th>
                                        <th>Kelas</th>
                                        <th>Jumlah</th>
                                        <th>Kemaskini Terakhir</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {studentList.map((s, i) => (
                                        <tr key={i}>
                                            <td>
                                                <button
                                                    className="btn btn-link text-decoration-none p-0 fw-semibold"
                                                    onClick={() => handleStudentClick(s)}
                                                >
                                                    {s.student_name || "-"}
                                                </button>
                                            </td>
                                            <td>{s.class_name || "-"}</td>
                                            <td>
                                                <span
                                                    className="badge bg-danger fs-6 px-3 py-2 rounded-pill"
                                                    style={{ fontWeight: "600", fontSize: "1rem" }}
                                                >
                                                    {s.total_bad_deeds || 0}
                                                </span>

                                            </td>

                                            <td>
                                                {s.last_recorded_date
                                                    ? new Date(s.last_recorded_date).toLocaleDateString("ms-MY")
                                                    : "-"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                {showModal && (
                    <div
                        className="modal fade show d-block"
                        tabIndex="-1"
                        role="dialog"
                        style={{ background: "rgba(0,0,0,0.4)" }}
                    >
                        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                            <div className="modal-content rounded-4 shadow">
                                <div className="modal-header">
                                    <h5 className="modal-title text-danger">
                                        Rekod Masalah Disiplin – {selectedStudent?.student_name}
                                    </h5>
                                    <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
                                </div>
                                <div className="modal-body">
                                    {studentDeeds.length === 0 ? (
                                        <p className="text-muted mb-0 text-center">Tiada rekod ditemui.</p>
                                    ) : (
                                        <table className="table table-sm table-striped align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Tarikh</th>
                                                    <th>Sebab</th>
                                                    <th>Catatan</th>
                                                    <th>Status</th>
                                                    <th>Guru</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {studentDeeds.map((d, i) => (
                                                    <tr key={i}>
                                                        <td>{new Date(d.date).toLocaleDateString("ms-MY")}</td>
                                                        <td>{d.deed_reason}</td>
                                                        <td>{d.remarks || "-"}</td>
                                                        <td>
                                                            <span
                                                                className={`badge ${d.status === "pending"
                                                                    ? "bg-warning text-dark"
                                                                    : "bg-success"
                                                                    }`}
                                                            >
                                                                {d.status}
                                                            </span>
                                                        </td>
                                                        <td>{d.teacher_name || "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                <div className="modal-footer d-flex justify-content-between">
                                    <button
                                        className="btn btn-outline-danger"
                                        onClick={async () => {
                                            try {
                                                const res = await downloadStudentDisciplinePDF(selectedStudent.student_uuid);

                                                const blob = new Blob([res.data], { type: "application/pdf" });
                                                const url = window.URL.createObjectURL(blob);

                                                const a = document.createElement("a");
                                                a.href = url;
                                                a.download = `Discipline_Report_${selectedStudent.student_name.replace(/ /g, "_")}.pdf`;
                                                a.click();
                                                window.URL.revokeObjectURL(url);

                                                toast.success("PDF berjaya dimuat turun");
                                            } catch (err) {
                                                toast.error("Gagal memuat turun PDF");
                                            }
                                        }}
                                    >
                                        Muat Turun PDF
                                    </button>

                                    <button className="btn btn-primary" onClick={() => setShowModal(false)}>
                                        Tutup
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                )}

                {loading && <div className="text-center text-muted">Loading...</div>}
            </div>
        </Fragment>
    );
};

export default AmalanBurukDashboard;
